import React, { useMemo, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  OutlinedInput,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Theme, withStyles } from '@material-ui/core/styles';
import { Clear as ClearIcon } from '@material-ui/icons';
import { FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';

import FormDateInput from 'components/FormDateInput';
import FormRichTextInput from 'components/FormRichTextInput';
import FormSelect from 'components/FormSelect';
import FormTextInput from 'components/FormTextInput';
import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import {
  ActionLibraryInstance,
  ActionLibraryInstanceCriticality,
  UpdateActionLibraryInstancePayload,
} from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import AssetSelectionList from 'pages/ActionLibraryInstance/ActionLibraryInstanceCreatePage/_components/Steps/AssetSelectionList/AssetSelectionList';
import PATHS from 'router/PATHS';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  stepContent: {
    flexGrow: 1,
  },
  stepCheckbox: {
    marginLeft: theme.spacing(2),
  },
  infoIcon: {
    cursor: 'help',
    color: theme.palette.action.active,
    fontSize: '1.2rem',
  },
  headerWithInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  inlineFields: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
}));

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 400,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

type Props = {
  instance: ActionLibraryInstance;
};

const ActionLibraryInstanceEditForm: React.FC<Props> = ({ instance }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const styles = useStyles();
  const [loading, setLoading] = useState(false);

  const { data: choicesData } = useQuery('actionTemplatesChoices', () => actionTemplatesApi.getChoices(), {
    staleTime: 1000 * 60 * 10,
  });

  const criticalityOptions = useMemo(() => {
    if (choicesData?.data?.criticality?.length) {
      return choicesData.data.criticality.map((c: { value: string; label: string }) => ({
        key: c.value,
        label: c.label,
      }));
    }
    return [
      { key: 'low', label: t(action_library_messages.criticality.LOW) },
      { key: 'medium', label: t(action_library_messages.criticality.MEDIUM) },
      { key: 'high', label: t(action_library_messages.criticality.HIGH) },
    ];
  }, [choicesData?.data?.criticality, t]);

  const validationSchema = yup.object({
    instanceName: yup.string().required(t(action_library_messages.instance.validation.instance_name_required)),
    deadlineDate: yup.date().required(t(action_library_messages.instance.validation.deadline_date_required)).nullable(),
    reminderDate: yup
      .date()
      .nullable()
      .transform((v, o) => (o === '' || o == null ? null : v))
      .test('is-before-deadline', t(action_library_messages.instance.validation.reminder_before_deadline), (value, context) => {
        const { deadlineDate } = context.parent;
        if (!deadlineDate || !value) return true;
        const formatDate = (d: unknown) => {
          if (d instanceof Date) return d.toISOString().split('T')[0];
          if (typeof d === 'string' && d.includes('T')) return d.split('T')[0];
          return String(d);
        };
        const dStr = formatDate(deadlineDate);
        const rStr = formatDate(value);
        return rStr <= dStr;
      }),
    selectedAssets: yup
      .array()
      .min(1, t(action_library_messages.instance.validation.assets_min))
      .required(t(action_library_messages.instance.validation.assets_required)),
  });

  const formik = useFormik({
    initialValues: {
      templateId: '',
      selectedTemplate: null,
      instanceName: instance.title,
      reportedTo: instance.reportedTo ?? instance.delegatedToFullName ?? '',
      deadlineDate: instance.deadline ? instance.deadline.split('T')[0] : '',
      reminderDate: instance.reminder ? instance.reminder.split('T')[0] : '',
      specialInstruction: instance.specialInstruction || '',
      criticality: instance.criticality || '',
      validityPeriodMonths: instance.validityPeriodMonths ?? null,
      stepOverrides:
        instance.steps?.map(step => ({
          id: step.id,
          mandatory: step.mandatory,
          title: step.title,
          instruction: step.instruction,
          order: step.order,
        })) || [],
      assetIds: instance.asset.toString(),
      selectedAssets: [
        {
          id: instance.asset,
          name: instance.assetName,
          key: '',
          category: null,
          owner: { id: instance.owner, email: '', fullName: instance.ownerFullName },
        } as any,
      ],
    },
    validationSchema,
    onSubmit: async values => {
      try {
        setLoading(true);

        const payload: UpdateActionLibraryInstancePayload = {
          title: values.instanceName,
          deadline: `${values.deadlineDate}T16:00:00.000Z`,
          ...(values.reminderDate && { reminder: `${values.reminderDate}T16:00:00.000Z` }),
          delegatedTo: instance.delegatedTo,
          reportedTo: values.reportedTo ?? null,
          specialInstruction: values.specialInstruction,
          steps: values.stepOverrides.map(step => ({
            id: step.id,
            mandatory: step.mandatory,
          })),
          assetId: Number(values.selectedAssets[0].id),
          ...(values.criticality && { criticality: values.criticality as ActionLibraryInstanceCriticality }),
          ...(values.validityPeriodMonths !== undefined && { validityPeriodMonths: values.validityPeriodMonths }),
        };

        await actionLibraryInstancesApi.updateActionLibraryInstance(instance.id, payload);
        enqueueSnackbar(t(action_library_messages.success.instance_updated), { variant: 'success' });
        history.push(PATHS.ACTION_LIBRARY_INSTANCE_DETAIL.replace(':id', instance.id.toString()));
      } catch (error: any) {
        enqueueSnackbar(t((action_library_messages.errors as any).instance_update_failed || 'Failed to update instance'), {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const validityDoesNotApply = formik.values.validityPeriodMonths === null;
  const handleValidityCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('validityPeriodMonths', e.target.checked ? null : formik.values.validityPeriodMonths ?? 0);
  };

  const handleMandatoryToggle = (stepId: number) => {
    const newOverrides = formik.values.stepOverrides.map(o => {
      if (o.id === stepId) {
        return { ...o, mandatory: !o.mandatory };
      }
      return o;
    });
    formik.setFieldValue('stepOverrides', newOverrides);
  };

  return (
    <FormikProvider value={formik as any}>
      <form className={styles.root} onSubmit={formik.handleSubmit}>
        <Paper style={{ padding: 24 }}>
          <Box className={styles.section}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormTextInput
                  formik={formik as any}
                  fullWidth
                  id='instanceName'
                  label={t(action_library_messages.instance.form.instance_name)}
                  required
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <FormDateInput
                  dateTime={false}
                  formik={formik as any}
                  fullWidth
                  id='deadlineDate'
                  label={t(action_library_messages.instance.form.deadline)}
                  required
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Box alignItems='flex-start' display='flex' position='relative'>
                  <FormDateInput
                    dateTime={false}
                    formik={formik as any}
                    fullWidth
                    id='reminderDate'
                    label={t(action_library_messages.instance.form.reminder)}
                  />
                  {formik.values.reminderDate && (
                    <Tooltip title={t(action_library_messages.instance.form.clear_reminder)}>
                      <IconButton
                        aria-label={t(action_library_messages.instance.form.clear_reminder)}
                        onClick={() => formik.setFieldValue('reminderDate', '')}
                        size='small'
                        style={{
                          position: 'absolute',
                          right: 4,
                          top: '50%',
                          transform: 'translateY(-50%)',
                        }}
                      >
                        <ClearIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
              <Grid item xs={4}>
                <div className={styles.inlineFields}>
                  <div style={{ flex: 2 }}>
                    <FormControl fullWidth variant='outlined'>
                      <InputLabel htmlFor='validityPeriodMonths'>{t(action_library_messages.properties.duration_in_months)}</InputLabel>
                      <OutlinedInput
                        disabled={validityDoesNotApply}
                        id='validityPeriodMonths'
                        inputProps={{ min: 0, step: 1, type: 'number' }}
                        label={t(action_library_messages.properties.duration_in_months)}
                        name='validityPeriodMonths'
                        onBlur={() => formik.setFieldTouched('validityPeriodMonths', true)}
                        onChange={e => {
                          const v = e.target.value;
                          formik.setFieldValue('validityPeriodMonths', v === '' ? null : Math.max(0, parseInt(String(v), 10) || 0));
                        }}
                        value={formik.values.validityPeriodMonths ?? ''}
                      />
                    </FormControl>
                  </div>
                  <div style={{ flex: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={validityDoesNotApply}
                          id='validityPeriodDoesNotApply'
                          name='validityPeriodDoesNotApply'
                          onChange={handleValidityCheckboxChange}
                        />
                      }
                      label={t(action_library_messages.properties.doesnt_apply)}
                    />
                  </div>
                </div>
              </Grid>
              <Grid item md={4} xs={12}>
                <FormTextInput
                  formik={formik as any}
                  fullWidth
                  id='reportedTo'
                  label={t(action_library_messages.instance.form.reported_to)}
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <FormSelect
                  formik={formik as any}
                  fullWidth
                  id='criticality'
                  label={t(action_library_messages.table.criticality)}
                  options={criticalityOptions}
                  size='small'
                />
              </Grid>
            </Grid>
          </Box>

          <Box className={styles.section}>
            <Typography style={{ marginBottom: '15px' }} variant='h6'>
              {t(action_library_messages.instance.form.special_instruction)}
            </Typography>
            <FormRichTextInput formik={formik as any} id='specialInstruction' />
          </Box>

          <Box className={styles.section}>
            <Typography gutterBottom variant='h6'>
              {t(action_library_messages.instance.form.steps_section_title)}
            </Typography>
            <Typography color='textSecondary' paragraph variant='body2'>
              {t(action_library_messages.instance.form.steps_section_description)}
            </Typography>
            <Paper style={{ padding: 16 }} variant='outlined'>
              {formik.values.stepOverrides.map(step => (
                <Box key={step.id} className={styles.stepRow}>
                  <Box className={styles.stepContent}>
                    <Typography variant='subtitle2'>{step.title}</Typography>
                    <Typography color='textSecondary' dangerouslySetInnerHTML={{ __html: step.instruction }} variant='body2' />
                  </Box>
                  <Box className={styles.stepCheckbox}>
                    <HtmlTooltip title={t(action_library_messages.instance.form.mandatory_tooltip)}>
                      <FormControlLabel
                        control={<Checkbox checked={step.mandatory} color='primary' onChange={() => handleMandatoryToggle(step.id)} />}
                        label={t(action_library_messages.instance.form.mandatory)}
                      />
                    </HtmlTooltip>
                  </Box>
                </Box>
              ))}
            </Paper>
          </Box>

          <Box className={styles.section}>
            <Typography gutterBottom variant='h6'>
              {t(action_library_messages.instance.asset_selection.title)}
            </Typography>
            <AssetSelectionList />
          </Box>

          <div className={styles.actions}>
            <Button disabled={loading} onClick={() => history.goBack()}>
              {t(action_library_messages.steps.cancel)}
            </Button>
            <Button color='primary' disabled={loading} type='submit' variant='contained'>
              {t(action_library_messages.success.save_changes)}
            </Button>
          </div>
        </Paper>
      </form>
    </FormikProvider>
  );
};

export default ActionLibraryInstanceEditForm;
